/**
 * Runs the writes for a planned upload and records what happened to every row.
 *
 * Outcomes come from the API response rather than inference: `src/api/client.ts`
 * attaches `status` and `code` to the error it throws, so a rejected write is
 * classified exactly instead of by matching on message text.
 *
 * Retries cover only 5xx and transport faults. A 4xx is a decision — "this company
 * already has one", "no such company" — and repeating it would just be noise.
 */

import type { PlannedRow, RowPlan } from "./planUpload.js";

export type Outcome =
  | "created"
  | "already-mapped"
  | "duplicate-in-file"
  | "malformed"
  | "not-found"
  | "rejected"
  | "writes-disabled"
  | "failed";

/** Coarse grouping behind the results filter chips. */
export type OutcomeGroup = "created" | "skipped" | "failed";

export type RowResult = {
  row: PlannedRow;
  outcome: Outcome;
  /** HTTP status, or null for a transport fault or a row that was never sent. */
  status: number | null;
  detail: string;
};

export type WriteFn = (companyUuid: string, referenceValue: string) => Promise<void>;

export type RunOptions = {
  concurrency?: number;
  attempts?: number;
  /**
   * Called after each row settles. `settled` carries the results so far, in input
   * order with gaps removed, so a caller can checkpoint progress somewhere durable
   * without waiting for the whole run.
   */
  onProgress?: (done: number, total: number, settled: RowResult[]) => void;
  /** Injectable so tests do not really wait out the backoff. */
  sleep?: (ms: number) => Promise<void>;
};

const DEFAULT_CONCURRENCY = 4;
const DEFAULT_ATTEMPTS = 3;
const BACKOFF_BASE_MS = 250;

const SKIPPED_OUTCOMES: Record<Exclude<RowPlan, "write">, Outcome> = {
  "already-mapped": "already-mapped",
  "duplicate-in-file": "duplicate-in-file",
  malformed: "malformed",
};

const STATUS_OUTCOMES: Record<number, Outcome> = {
  409: "already-mapped",
  404: "not-found",
  403: "writes-disabled",
  400: "rejected",
};

export function outcomeGroup(outcome: Outcome): OutcomeGroup {
  if (outcome === "created") {
    return "created";
  }
  if (outcome === "already-mapped" || outcome === "duplicate-in-file" || outcome === "malformed") {
    return "skipped";
  }
  return "failed";
}

function readError(error: unknown): {
  status: number | null;
  code: string | null;
  message: string;
} {
  if (error instanceof Error) {
    const { status, code } = error as Error & { status?: number; code?: string };
    return {
      status: typeof status === "number" ? status : null,
      code: typeof code === "string" ? code : null,
      message: error.message,
    };
  }
  return { status: null, code: null, message: String(error) };
}

/** True when the failure is worth another attempt: 5xx, or no response at all. */
function isTransient(status: number | null): boolean {
  return status === null || status >= 500;
}

function describeSkip(row: PlannedRow): string {
  switch (row.plan) {
    case "already-mapped":
      return row.existingValue
        ? `Already holds ${row.existingValue}`
        : "Already holds a reference of this type";
    case "duplicate-in-file":
      return "A later row for a company already listed in this file";
    case "malformed":
      return row.problem === "invalid-uuid" ? "company_uuid is not valid" : "Value is not valid";
    default:
      return "";
  }
}

async function writeRow(
  row: PlannedRow,
  write: WriteFn,
  attempts: number,
  sleep: (ms: number) => Promise<void>,
): Promise<RowResult> {
  let last = { status: null as number | null, message: "" };

  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      await write(row.companyUuid, row.referenceValue);
      return { row, outcome: "created", status: 201, detail: "" };
    } catch (error) {
      const { status, code, message } = readError(error);
      last = { status, message };

      const mapped = status === null ? undefined : STATUS_OUTCOMES[status];
      if (mapped) {
        return {
          row,
          outcome: mapped,
          status,
          detail: code ? `${message} (${code})` : message,
        };
      }
      if (!isTransient(status)) {
        // An unrecognised 4xx is still the API refusing, not a fault to retry.
        return { row, outcome: "rejected", status, detail: message };
      }
      if (attempt < attempts) {
        await sleep(BACKOFF_BASE_MS * 2 ** (attempt - 1));
      }
    }
  }

  return { row, outcome: "failed", status: last.status, detail: last.message };
}

export async function runUpload(
  rows: PlannedRow[],
  write: WriteFn,
  options: RunOptions = {},
): Promise<RowResult[]> {
  const concurrency = Math.max(1, options.concurrency ?? DEFAULT_CONCURRENCY);
  const attempts = Math.max(1, options.attempts ?? DEFAULT_ATTEMPTS);
  const sleep = options.sleep ?? (ms => new Promise(resolve => setTimeout(resolve, ms)));

  // Results are written back by index so the caller gets input order regardless of
  // which requests finish first.
  const results = new Array<RowResult>(rows.length);
  let next = 0;
  let done = 0;
  // A 403 means the address-book-live flag is off, so every remaining row would fail
  // identically. Stop attempting them — one clear reason beats hundreds of copies of it.
  let writesDisabled = false;

  const worker = async (): Promise<void> => {
    while (next < rows.length) {
      const index = next;
      next += 1;
      const row = rows[index] as PlannedRow;

      if (row.plan !== "write") {
        results[index] = {
          row,
          outcome: SKIPPED_OUTCOMES[row.plan],
          status: null,
          detail: describeSkip(row),
        };
      } else if (writesDisabled) {
        results[index] = {
          row,
          outcome: "writes-disabled",
          status: null,
          detail: "Not attempted — writes are disabled",
        };
      } else {
        const result = await writeRow(row, write, attempts, sleep);
        if (result.outcome === "writes-disabled") {
          writesDisabled = true;
        }
        results[index] = result;
      }

      done += 1;
      // Compacted because `results` is filled by index and so has gaps while workers
      // are still in flight; a caller persisting this must not see holes.
      options.onProgress?.(done, rows.length, results.filter(Boolean));
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, rows.length) }, () => worker()));

  return results;
}
